import React, { useState, useEffect } from "react";
import "./PopupHandlers.css";
import config from "../../../../config";
import fetchWithAuth from "../../../../services/fetchWithAuth";
import { useNavigate } from "react-router-dom";
const BASE_API = config.API_BASE_URL;

interface TableRow {
  siNumber: number;
  invitee_name: string;
  email: string;
  primary_division: number | null;
  secondary_division: number | null;
}

interface DivisionOption {
  id: number;
  text: string;
}

interface DivisionOptions {
  id: number;
  divisionText: string;
  divisions: DivisionOption[];
}

const transformDivisionData = (data: any[]): DivisionOptions[] => {
  return data.map((division: any) => ({
    id: division.id,
    divisionText: division.division_name,
    divisions: division.secondary_divisions.map((subDivision: any) => ({
      id: subDivision.id,
      text: subDivision.division_name,
    })),
  }));
};

export const ManualUploadPopupBody: React.FC<{
  onClose: () => void;
  fundraiserID: number;
}> = ({ onClose, fundraiserID }) => {
  const [rows, setRows] = useState<TableRow[]>([
    {
      siNumber: 1,
      invitee_name: "",
      email: "",
      primary_division: null,
      secondary_division: null,
    },
  ]);
  const [nextSiNumber, setNextSiNumber] = useState<number>(2);
  const [divisionOptions, setDivisionOptions] = useState<DivisionOptions[]>([]);

  useEffect(() => {
    const fetchDivisionOptions = async () => {
      try {
        const response = await fetchWithAuth(
          `${BASE_API}division/fundraiser/${fundraiserID}`
        );
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        const transformedData = transformDivisionData(data);
        setDivisionOptions(transformedData);
      } catch (error) {
        console.error("Error fetching division options:", error);
      }
    };

    fetchDivisionOptions();
  }, [fundraiserID]);

  const addRow = (): void => {
    setRows([
      ...rows,
      {
        siNumber: nextSiNumber,
        invitee_name: "",
        email: "",
        primary_division: null,
        secondary_division: null,
      },
    ]);
    setNextSiNumber(nextSiNumber + 1);
  };

  const deleteRow = (index: number): void => {
    const newRows = rows.filter((_, i) => i !== index);
    const updatedRows = newRows.map((row, i) => ({
      ...row,
      siNumber: i + 1,
    }));
    setRows(updatedRows);
    setNextSiNumber(updatedRows.length + 1);
  };

  const handleChange = (
    index: number,
    field: keyof TableRow,
    value: string | number | null
  ): void => {
    const newRows = [...rows];
    newRows[index] = { ...newRows[index], [field]: value };
    if (field === "primary_division") {
      newRows[index].secondary_division = null;
    }
    setRows(newRows);
  };

  const handleprimary_divisionChange = (
    index: number,
    value: number | null
  ): void => {
    handleChange(index, "primary_division", value);
  };

  const handleTextChange = (
    index: number,
    field: "invitee_name" | "email",
    value: string
  ): void => {
    handleChange(index, field, value);
  };

  const getDivisionOptions = (divisionId: number | null): DivisionOption[] => {
    const division = divisionOptions.find((d) => d.id === divisionId);
    return division ? division.divisions : [];
  };

  const handleOkayClick = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const url = `${BASE_API}fundraiser_roles/participant-invites`;
    const data = {
      fundraiser_id: fundraiserID,
      invites: rows,
    };

    try {
      const response = await fetchWithAuth(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const result = await response.json();
    } catch (error: any) {
      console.error("Error:", error);
    } finally {
      onClose();
    }
  };

  return (
    <div className="popup-handlers-table-popup">
      <form onSubmit={handleOkayClick}>
        <table>
          <thead>
            <tr>
              <th>SI#</th>
              <th>Name</th>
              <th>Email</th>
              <th>Primary Division</th>
              <th>Secondary Division</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                <td>{row.siNumber}</td>
                <td>
                  <input
                    type="text"
                    value={row.invitee_name}
                    required
                    onChange={(e) =>
                      handleTextChange(index, "invitee_name", e.target.value)
                    }
                  />
                </td>
                <td>
                  <input
                    type="email"
                    value={row.email}
                    required
                    onChange={(e) =>
                      handleTextChange(index, "email", e.target.value)
                    }
                  />
                </td>
                <td>
                  <select
                    value={row.primary_division ?? ""}
                    onChange={(e) =>
                      handleprimary_divisionChange(
                        index,
                        e.target.value ? Number(e.target.value) : null
                      )
                    }
                  >
                    <option value="">Select Primary Division</option>
                    {divisionOptions.map((division) => (
                      <option key={division.id} value={division.id}>
                        {division.divisionText}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    value={row.secondary_division ?? ""}
                    onChange={(e) =>
                      handleChange(
                        index,
                        "secondary_division",
                        e.target.value ? Number(e.target.value) : null
                      )
                    }
                    disabled={row.primary_division === null}
                  >
                    <option value="">Select Secondary Division</option>
                    {getDivisionOptions(row.primary_division).map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.text}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <button type="button" onClick={() => deleteRow(index)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button type="button" onClick={addRow}>
          Add Row
        </button>
        <button type="submit">OK</button>
      </form>
    </div>
  );
};
export const AssignAdminPopupBody: React.FC = () => {
  return (
    <div className="popup-handlers-admin-popup-body">
      Great Job! Click continue and your team admins will receive an email to
      add participants to their team.
    </div>
  );
};

interface UploadExcelPopupBodyProps {
  fundraiser_id: number;
  onClose: () => void;
}

export const UploadExcelPopupBody: React.FC<UploadExcelPopupBodyProps> = ({
  fundraiser_id,
  onClose,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [uploadComplete, setUploadComplete] = useState<boolean>(false);
  const [successList, setSuccessList] = useState<
    Array<{ first_name: string; last_name: string; email: string }>
  >([]);
  const [errorList, setErrorList] = useState<
    Array<{
      first_name: string;
      last_name: string;
      email: string;
      failed_reasoning: string;
    }>
  >([]);
  const navigate = useNavigate();
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setFile(event.target.files[0]);
    }
  };

  const uploadExcelPopupOkayButton = async (): Promise<void> => {
    if (!file) {
      alert("Please select a file first.");
      return;
    }

    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("fundraiser", fundraiser_id.toString());

      const response = await fetchWithAuth(
        BASE_API + "upload-excel/participant-invite/",
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();
      setSuccessList(data.successList || []);
      setErrorList(data.errorList || []);
      setUploadComplete(true);
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("There was an error uploading the file.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddMoreParticipants = (): void => {
    onClose();
  };

  const handleDoneEnteringParticipants = (): void => {
    onClose();
    //NEXT PAGE HAS NOT BEEN CREATED YET
    navigate("/undefined");
  };

  return (
    <div className="popup-handlers-excel-popup-body">
      {!uploadComplete ? (
        <>
          <label htmlFor="fileUpload">Choose an Excel file:</label>
          <input
            id="fileUpload"
            type="file"
            accept=".xlsx, .xls"
            onChange={handleFileChange}
            style={{ display: "block", margin: "10px 0" }}
          />

          <button
            onClick={uploadExcelPopupOkayButton}
            disabled={!file || isLoading}
          >
            Upload
          </button>

          {isLoading && <p>Please wait, uploading...</p>}
        </>
      ) : (
        <div className="popup-handlers-buttons-container">
          <button
            className="popup-handlers-complete-button"
            onClick={handleAddMoreParticipants}
          >
            Add more Participants
          </button>
          <button
            className="popup-handlers-complete-button"
            onClick={handleDoneEnteringParticipants}
          >
            Done Entering Participants
          </button>
        </div>
      )}

      {!isLoading && (successList.length > 0 || errorList.length > 0) && (
        <div className="popup-handlers-upload-results">
          {successList.length > 0 && (
            <div className="popup-handlers-upload-table">
              <h3>Successful Entries</h3>
              <p>Count: {successList.length}</p>
              <div className="popup-handlers-table-container">
                <ul>
                  {successList.map((item, index) => (
                    <li key={index}>
                      • {item.first_name} {item.last_name} ({item.email})
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {errorList.length > 0 && (
            <div className="popup-handlers-upload-table">
              <h3>Unsuccessful Entries</h3>
              <p>Count: {errorList.length}</p>
              <div className="popup-handlers-table-container">
                <ul>
                  {errorList.map((item, index) => (
                    <li key={index}>
                      • {item.first_name} {item.last_name} ({item.email} -{" "}
                      {item.failed_reasoning})
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
